import React, { useState, useRef, useEffect } from "react";

import {
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { EditOutlined, Visibility, VisibilityOff } from "@mui/icons-material";

import { PageStandard } from "components/admin/PageStandard";
import { BlockStandard } from "components/admin/BlockStandard";
import { BuildingProperties } from "api/onboardingTypes";
import { CustomSelect } from "components/onboarding/customSelect";
import { OnboardingSelectorOptions } from "libLegacy/api/onboarding/onboardingModels";
import {
  createEnergyOperatorCredentials,
  fetchEnergyOperatorValues,
} from "libLegacy/api/onboarding/onboarding";
import { useToken } from "contexts/UserContext";
import { useSetError } from "contexts/ErrorContext";

const VOLTAGE_MAP: Record<BuildingProperties["voltageLevel"], string> = {
  groupA: "Média/Alta tensão (Grupo A)",
  groupB: "Baixa tensão (Grupo B)",
  idk: "Não sei",
};

export const InstalationsCreate = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [clockName, setClockName] = useState<string>("");
  const token = useToken();
  const setGlobalError = useSetError();
  const [voltage, setVoltage] = useState<
    BuildingProperties["voltageLevel"] | null
  >(null);
  const [installCode, setInstallCode] = useState<string>("");
  const [hidePassword, setHidePassword] = useState<boolean>(true);
  const [operators, setOperators] =
    useState<Array<OnboardingSelectorOptions>>();
  const [chosenOperatorId, setChosenOperatorId] =
    useState<OnboardingSelectorOptions["id"]>();

  const refEmail = useRef<HTMLInputElement>(null);
  const refPassword = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadSelectData = async (token) => {
      try {
        const data = await fetchEnergyOperatorValues(token);
        setOperators(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    loadSelectData(token);
  }, [token, setGlobalError]);

  const toggleVisibility = () => {
    setHidePassword((prev) => !prev);
  };

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const desiredPlatform = operators?.find(
        ({ id }) => id === chosenOperatorId
      );
      const formData = {
        email: refEmail.current?.value || "",
        password: refPassword.current?.value || "",
        desiredPlatform,
        idOperator: chosenOperatorId,
        tensionLevel: voltage,
        installationCode: installCode,
      };
      await createEnergyOperatorCredentials(token, formData);
    } catch (error) {
      // @ts-expect-error
      setGlobalError(error.response?.data?.error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageStandard title="Instalações" hideDivider>
      <BlockStandard>
        <TextField
          label="Nome do relógio"
          InputProps={{
            endAdornment: <EditOutlined />,
          }}
          value={clockName}
          onChange={({ target }) => setClockName(target.value)}
        />
        <Autocomplete
          clearOnBlur
          value={voltage}
          getOptionLabel={(key) => VOLTAGE_MAP[key]}
          // @ts-expect-error wont't be a random string
          onChange={(e, newValue) => setVoltage(newValue)}
          options={Object.keys(VOLTAGE_MAP)}
          renderInput={(params) => (
            <TextField label="Tensão" {...params} variant="outlined" />
          )}
        />
        <TextField
          label="Código de instalação"
          value={installCode}
          onChange={({ target }) => setInstallCode(target.value)}
        />
        <CustomSelect
          label="Distribuidora de energia"
          value={chosenOperatorId}
          onChange={({ target: { value } }) => {
            setChosenOperatorId(value);
          }}
          options={operators || []}
        />
        <TextField inputRef={refEmail} label="Seu login" variant="outlined" />
        <TextField
          inputRef={refPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={toggleVisibility}>
                  {hidePassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          label="Senha de acesso"
          type={hidePassword ? "password" : "text"}
          variant="outlined"
        />
      </BlockStandard>

      <BlockStandard hideDivider>
        <Button variant="contained" onClick={onSubmit} disabled={isLoading}>
          Enviar dados da distribuidora
        </Button>
      </BlockStandard>
    </PageStandard>
  );
};
